export const DONOR_LIST_VIEW_STATE_KEY = "donorListViewState";

export interface DonorListOrderBy {
  field: string;
  direction: string;
}

export interface DonorListViewState {
  version: 1;
  page: number;
  perPage: number;
  orderBy: DonorListOrderBy;
  checkedIds: Array<number | string>;
  scrollY: number;
  lastDonorId?: number | string | null;
}

const PREFERENCES_VERSION = 1 as const;

function toPositiveInt(value: unknown, fallback: number): number {
  const parsed = typeof value === "number" ? value : Number(value);

  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return Math.round(parsed);
}

function sanitizeCheckedIds(value: unknown): Array<number | string> {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();
  const ids: Array<number | string> = [];

  for (const item of value) {
    if (typeof item !== "number" && typeof item !== "string") {
      continue;
    }

    if (item === "" || (typeof item === "number" && !Number.isFinite(item))) {
      continue;
    }

    const key = String(item);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    ids.push(item);
  }

  return ids;
}

function sanitizeOrderBy(value: unknown): DonorListOrderBy {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { field: "", direction: "" };
  }

  const orderBy = value as Partial<DonorListOrderBy>;

  return {
    field: typeof orderBy.field === "string" ? orderBy.field : "",
    direction: typeof orderBy.direction === "string" ? orderBy.direction : "",
  };
}

function sanitizeLastDonorId(
  value: unknown
): number | string | null | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === "") {
    return null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value;
  }

  return null;
}

export function loadDonorListViewState(
  storageKey: string = DONOR_LIST_VIEW_STATE_KEY
): DonorListViewState | null {
  if (typeof sessionStorage === "undefined") {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(storageKey);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<DonorListViewState>;

    if (!parsed || parsed.version !== PREFERENCES_VERSION) {
      return null;
    }

    return {
      version: PREFERENCES_VERSION,
      page: toPositiveInt(parsed.page, 1),
      perPage: toPositiveInt(parsed.perPage, 20),
      orderBy: sanitizeOrderBy(parsed.orderBy),
      checkedIds: sanitizeCheckedIds(parsed.checkedIds),
      scrollY:
        typeof parsed.scrollY === "number" &&
        Number.isFinite(parsed.scrollY) &&
        parsed.scrollY >= 0
          ? parsed.scrollY
          : 0,
      lastDonorId: sanitizeLastDonorId(parsed.lastDonorId),
    };
  } catch {
    return null;
  }
}

export function saveDonorListViewState(
  state: DonorListViewState,
  storageKey: string = DONOR_LIST_VIEW_STATE_KEY
): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  sessionStorage.setItem(
    storageKey,
    JSON.stringify({
      version: PREFERENCES_VERSION,
      page: toPositiveInt(state.page, 1),
      perPage: toPositiveInt(state.perPage, 20),
      orderBy: sanitizeOrderBy(state.orderBy),
      checkedIds: sanitizeCheckedIds(state.checkedIds),
      scrollY:
        typeof state.scrollY === "number" &&
        Number.isFinite(state.scrollY) &&
        state.scrollY >= 0
          ? state.scrollY
          : 0,
      lastDonorId: sanitizeLastDonorId(state.lastDonorId) ?? null,
    })
  );
}

export function clearDonorListViewState(
  storageKey: string = DONOR_LIST_VIEW_STATE_KEY
): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  sessionStorage.removeItem(storageKey);
}
